import { Phonelink as MobileLinksIcon } from "@mui/icons-material";
import { FormControlLabel, Switch, TextField } from "@mui/material";
import React, { useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";

import { AdminDialog } from "@/components/common";
import MuiAutocompleteMultiSelect from "@/components/common/MuiAutocompleteMultiSelect";
import { useAuth } from "@/contexts/AuthContext";
import MobileLinkService from "@/services/features/MobileLinkService";
import { Store, StoreService } from "@/services/features/StoreService";
import { formatStoreSelectorLabel } from "@/utils/store";
import { MobileLink } from "@/types/mobileLink";

interface MobileLinkFormProps {
  open: boolean;
  onClose: () => void;
  mobileLink?: MobileLink | null; // Optional, for editing
  customerId: number; // Required for creating new links
  onSave: () => void; // Callback after successful save
}

const MobileLinkForm: React.FC<MobileLinkFormProps> = ({
  open,
  onClose,
  mobileLink,
  customerId,
  onSave,
}) => {
  const { t } = useTranslation();
  const { user } = useAuth();
  const storeService = useMemo(() => StoreService.getInstance(), []);

  const [formData, setFormData] = useState<Partial<MobileLink>>({
    name: "",
    storeId: undefined,
    enabled: true,
  });
  const [loading, setLoading] = useState(false);
  const [stores, setStores] = useState<Store[]>([]);
  const [storesLoading, setStoresLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (mobileLink) {
      setFormData({
        id: mobileLink.id,
        name: mobileLink.name,
        storeId: mobileLink.storeId,
        enabled: mobileLink.enabled,
      });
    } else {
      setFormData({
        name: "",
        storeId: undefined,
        enabled: true,
      });
    }
    setError(null);
  }, [mobileLink, open]);

  useEffect(() => {
    const fetchStores = async () => {
      setStoresLoading(true);
      try {
        const response = await storeService.getStoresByCustomer(customerId);
        if (response.data) {
          setStores(response.data);
        } else {
          setStores([]);
        }
      } catch (err) {
        setError(
          err instanceof Error
            ? err.message
            : t("features.mobileLink.mobileLinkForm.errors.failedFetchStores"),
        );
        setStores([]);
      } finally {
        setStoresLoading(false);
      }
    };

    if (customerId) {
      fetchStores();
    }
  }, [customerId, storeService, t]);

  const handleInputChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const { name, type, value, checked } = event.target;
    setFormData((prev) => ({
      ...prev,
      [name]: type === "checkbox" ? checked : value,
    }));
  };

  const handleSubmit = async () => {
    setLoading(true);
    setError(null);
    try {
      const trimmedName = (formData.name ?? "").trim();
      if (!trimmedName) {
        throw new Error(t("features.mobileLink.mobileLinkForm.errors.nameRequired"));
      }

      if (mobileLink) {
        // Edit existing mobile link
        if (formData.id === undefined) {
          throw new Error(t("features.mobileLink.mobileLinkForm.errors.missingIdForUpdate"));
        }
        await MobileLinkService.update({
          ...formData,
          name: trimmedName,
        } as MobileLink);
      } else {
        // Create new mobile link
        if (formData.storeId === undefined) {
          throw new Error(t("features.mobileLink.mobileLinkForm.errors.storeRequired"));
        }
        await MobileLinkService.create({
          ...formData,
          customerId,
          name: trimmedName,
        } as MobileLink);
      }
      onSave();
      onClose();
    } catch (err) {
      setError(
        err instanceof Error
          ? err.message
          : t("features.mobileLink.mobileLinkForm.errors.unexpectedErrorOccurred"),
      );
    } finally {
      setLoading(false);
    }
  };

  return (
    <AdminDialog
      open={open}
      onClose={onClose}
      maxWidth="sm"
      title={
        mobileLink
          ? t("features.mobileLink.mobileLinkForm.title.editMobileLink")
          : t("features.mobileLink.mobileLinkForm.title.createMobileLink")
      }
      icon={<MobileLinksIcon />}
      error={error}
      cancelAction={{ label: t("common.cancel"), onClick: onClose, disabled: loading }}
      primaryAction={{
        label: mobileLink
          ? t("features.mobileLink.mobileLinkForm.actions.saveChanges")
          : t("features.mobileLink.mobileLinkForm.actions.create"),
        onClick: () => void handleSubmit(),
        loading,
      }}
    >
      <TextField
        label={t("features.mobileLink.mobileLinkForm.fields.name")}
        name="name"
        value={formData.name ?? ""}
        onChange={handleInputChange}
        fullWidth
        required
        sx={{ mb: 2 }}
      />
      <MuiAutocompleteMultiSelect
        label={t("features.mobileLink.mobileLinkForm.fields.store")}
        value={stores.filter((store) => store.id === formData.storeId)}
        options={stores}
        onChange={(next) => {
          setFormData((prev) => ({
            ...prev,
            storeId: next[0]?.id,
          }));
        }}
        getOptionLabel={(store) => formatStoreSelectorLabel(store, t, user?.role, "name")}
        getOptionKey={(store) => store.id}
        required
        disabled={storesLoading}
        loading={storesLoading}
        helperText={
          !storesLoading && stores.length === 0
            ? t("features.mobileLink.mobileLinkForm.empty.noStoresAvailable")
            : undefined
        }
        sx={{ mb: 2 }}
      />
      <FormControlLabel
        control={
          <Switch
            checked={Boolean(formData.enabled)}
            onChange={handleInputChange}
            name="enabled"
            color="primary"
          />
        }
        label={t("features.mobileLink.mobileLinkForm.fields.enabled")}
        sx={{ mb: 2 }}
      />
    </AdminDialog>
  );
};

export default MobileLinkForm;
